import assessmentReader from '../../../store/readHelpers/AssessmentReader';
import { t } from '../../../services/i18nService';
import backgroundAudioService from '../../../services/BackgroundAudioService';
import backgroundAudioStatus from '../../../constants/BackgroundAudioStatus';
import sessionReader from '../../../store/readHelpers/SessionReader';
import settingsReader from '../../../store/readHelpers/SettingsReader';

const backgroundAudioControlButtonSelector = {
    selectButtonCaption(currentSectionAudioStatus, isPausingAllowed, backgroundAudioReplayAllowed, sectionNumber) {
        switch (currentSectionAudioStatus) {
        case backgroundAudioStatus.PLAYING:
            if (settingsReader.shouldAllowPauseAndResumeOptionForTestTaker()) {
                return t('multimedia.actionButton.pause');
            }
            return isPausingAllowed
                ? t('header.backgroundAudioControl.pausePart', { number: sectionNumber })
                : t('header.backgroundAudioControl.playPart', { number: sectionNumber });
        case backgroundAudioStatus.ENDED:
            return backgroundAudioReplayAllowed
                ? t('header.backgroundAudioControl.replayPart', { number: sectionNumber })
                : t('header.backgroundAudioControl.playPart', { number: sectionNumber });
        default:
            return settingsReader.shouldAllowPauseAndResumeOptionForTestTaker() ? t('session.playAudio') :
                t('header.backgroundAudioControl.playPart', { number: sectionNumber });
        }
    },

    selectButtonIcon(currentSectionAudioStatus, isPausingAllowed, backgroundAudioReplayAllowed) {
        if (
            currentSectionAudioStatus === backgroundAudioStatus.PLAYING &&
            (settingsReader.shouldAllowPauseAndResumeOptionForTestTaker() || isPausingAllowed)
        ) {
            return 'fa-pause-circle';
        }

        if (currentSectionAudioStatus === backgroundAudioStatus.ENDED && backgroundAudioReplayAllowed) {
            return 'fa-repeat';
        }

        return 'fa-play-circle';
    },

    selectIsButtonDisabled(backgroundAudio, currentSectionAudioStatus, isPausingAllowed, replaysLeft) {
        if (sessionReader.isPaused()) {
            return true;
        }
        const hasPlayingSectionAudio = backgroundAudioService.hasPlayingSectionAudio(backgroundAudio);
        return (hasPlayingSectionAudio && !isPausingAllowed) ||
            (currentSectionAudioStatus === backgroundAudioStatus.ENDED && replaysLeft <= 0);
    },

    selectAudioStartTime(sectionBackgroundAudioPart, backgroundAudio, currentSectionAudioStatus, replaysLeft) {
        if (currentSectionAudioStatus === backgroundAudioStatus.PLAYING) {
            return null;
        }

        if (!sectionBackgroundAudioPart) {
            return 0;
        }

        if (currentSectionAudioStatus === backgroundAudioStatus.ENDED && replaysLeft > 0) {
            return sectionBackgroundAudioPart.startTime;
        }

        const audioData = backgroundAudio[sectionBackgroundAudioPart.contentItemId];
        if (audioData?.position) {
            return audioData.position;
        }

        if (assessmentReader.isFirstSectionNumber() && !backgroundAudio.isIntroPlayed) {
            const intro = backgroundAudioService.getIntroBackgroundAudioPart();
            return intro.startTime || 0;
        }

        return sectionBackgroundAudioPart.startTime || 0;
    },

    selectAudioEndTime(sectionBackgroundAudioPart) {
        return sectionBackgroundAudioPart ? sectionBackgroundAudioPart.startTime + sectionBackgroundAudioPart.duration : 0;
    },

    selectReplaysStatusText(backgroundAudioReplayAllowed, replaysLeft) {
        if (!backgroundAudioReplayAllowed) {
            return null;
        }

        if (!replaysLeft) {
            return t('header.backgroundAudioControl.noReplaysRemaining');
        }

        return replaysLeft === 1
            ? t('header.backgroundAudioControl.oneReplayRemaining')
            : t('header.backgroundAudioControl.replaysRemaining', { amount: replaysLeft });
    },

    selectProps(state) {
        const {
            assessment,
            backgroundAudio,
            ui,
        } = state;

        const sectionBackgroundAudioPart = backgroundAudioService.getCurrentSectionBackgroundAudioPart();
        const currentSectionAudioStatus = settingsReader.shouldAllowPauseAndResumeOptionForTestTaker() ?
            ui.backgroundAudioStatus : backgroundAudioService.getCurrentSectionAudioStatus();
        const isPausingAllowed = sessionReader.isBackgroundAudioPausingAllowed();
        const timesReplayRequested = backgroundAudioService.getCurrentSectionAudioTimesReplayRequested();
        const replaysLeft = sessionReader.getBackgroundAudioReplaysMaxAmount() - timesReplayRequested;
        const backgroundAudioReplayAllowed = sessionReader.isBackgroundAudioReplayAllowed();
        const sectionNumber = assessmentReader.getCurrentSectionNumber();

        return {
            assessment,
            audioEndTime: this.selectAudioEndTime(sectionBackgroundAudioPart),
            audioStartTime: this.selectAudioStartTime(sectionBackgroundAudioPart, backgroundAudio, currentSectionAudioStatus, replaysLeft),
            audioStatus: currentSectionAudioStatus,
            backgroundAudioStarted: ui.backgroundAudioStarted,
            buttonCaption: this.selectButtonCaption(currentSectionAudioStatus, isPausingAllowed, backgroundAudioReplayAllowed, sectionNumber),
            buttonIcon: this.selectButtonIcon(currentSectionAudioStatus, isPausingAllowed, backgroundAudioReplayAllowed),
            isButtonDisabled: this.selectIsButtonDisabled(backgroundAudio, currentSectionAudioStatus, isPausingAllowed, replaysLeft),
            replaysStatusText: this.selectReplaysStatusText(backgroundAudioReplayAllowed, replaysLeft),
            sectionBackgroundAudioPart,
            sectionNumber,
            timesReplayRequested,
        };
    },
};

export default backgroundAudioControlButtonSelector;
